import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { radius, type } from "../../theme/tokens";

/**
 * What a list shows when it has nothing to show.
 *
 * Built from an icon, type and a tinted disc — no illustration, no artwork, no
 * image asset of any kind.
 *
 * The `variant` split matters more than it looks. A list that failed to load
 * and a list that is genuinely empty are different facts, and the portal has a
 * long-standing habit of rendering both as the same blank grid (see the Client
 * List defect in CLAUDE.md). Anywhere this component is used, the caller is
 * forced to decide which one it is.
 */
const EmptyState = ({
  title,
  description,
  icon,
  action,
  variant = "empty",
  dense = false,
}: {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  /** `error` when a request failed; `empty` when it succeeded with no rows. */
  variant?: "empty" | "error";
  dense?: boolean;
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const isError = variant === "error";
  const accent = isError ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Box
      role={isError ? "alert" : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: dense ? 4 : 7,
        px: 3,
        gap: 0.5,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: dense ? 44 : 56,
          height: dense ? 44 : 56,
          mb: 1.5,
          display: "grid",
          placeItems: "center",
          borderRadius: `${radius.lg}px`,
          color: accent,
          backgroundColor: alpha(accent, isLight ? 0.08 : 0.16),
          border: `1px solid ${alpha(accent, isLight ? 0.18 : 0.28)}`,
          "& .MuiSvgIcon-root": { fontSize: dense ? 22 : 26 },
        }}
      >
        {icon ?? (isError ? <ErrorOutlineRoundedIcon /> : <InboxRoundedIcon />)}
      </Box>

      <Typography
        sx={{
          fontSize: dense ? 14 : 15.5,
          fontWeight: 660,
          color: theme.palette.text.secondary,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            fontSize: type.body,
            color: theme.palette.text.primary,
            maxWidth: 380,
            lineHeight: 1.55,
          }}
        >
          {description}
        </Typography>
      )}

      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
};

export default EmptyState;
